<script setup>
import { ref, computed, onMounted } from "vue";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Autocomplete from "@/components/livelihood/forms/elements/Autocomplete.vue";
import { useRoute, useRouter } from "vue-router";
import { useListStore, useLivelihoodStore, useUserStore } from "@/stores";
import currentlyWorkingStatuses from "@/config/lists/currentlyWorkingStatuses";

const route = useRoute();
const router = useRouter();
const listStore = useListStore();
const userStore = useUserStore();
const livelihoodStore = useLivelihoodStore();

const user = userStore.user;

const courses = computed(() => listStore.emplServices);
const branches = computed(() => listStore.branches);
const emplOfficers = computed(() => listStore.employabilityOfficers);
const companies = computed(() => listStore.companies);
const emplFields = computed(() => listStore.employmentFields);
const iscoCodes = computed(() => listStore.iscoCodes);
const professionalFields = computed(() => listStore.professionalFields);

import {
  BaseKit,
  Bold,
  Color,
  Fullscreen,
  Heading,
  Highlight,
  History,
  Image,
  Italic,
  Link,
  Strike,
  Table,
  Underline,
  Video,
  VuetifyTiptap,
} from "vuetify-pro-tiptap";
import "vuetify-pro-tiptap/style.css";
import countOptions from "@/config/lists/countOptions";
import employmentStatuses from "@/config/lists/employmentStatuses";
import jobFoundMethods from "@/config/lists/jobFoundMethods";
import trainingRelationStatuses from "@/config/lists/trainingRelationStatuses";
import contractTypes from "@/config/lists/contractTypes";
import contractDurations from "@/config/lists/contractDurations";
import salaryRange from "@/config/lists/salaryRange";
import workSatisfactionOptions from "@/config/lists/workSatisfactionOptions";
import { formatDateToIsoString } from "@/helpers/helpers";

const extensions = [
  BaseKit.configure({
    placeholder: {
      placeholder: "Enter some text...",
    },
  }),
  Bold,
  Italic,
  Underline,
  Strike,
  Color,
  Highlight,
  Heading,
  Link,
  Image,
  Video,
  Table,
  Fullscreen,
  History,
];

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  data.date = formatDateToIsoString(data.date);

  if (data.jobStartDate != null) {
    data.jobStartDate = formatDateToIsoString(data.jobStartDate);
  }

  if (data.jobEndDate != null) {
    data.jobEndDate = formatDateToIsoString(data.jobEndDate);
  }

  if (isValid) {
    livelihoodStore.editLivelihood({ id: data.id, data });
  }
};

const rules = {
  name: {
    required: helpers.withMessage("Title is required", required),
  },
  beneficiary: {
    required: helpers.withMessage("Beneficiary is required", required),
  },
  branch: {
    required: helpers.withMessage("Branch is required", required),
  },
  responsibleOfficer: {
    required: helpers.withMessage("Responsible User is required", required),
  },
  currentlyWorking: {
    required: helpers.withMessage("Current Work Status is required", required),
  },
  date: {
    required: helpers.withMessage("Date is required", required),
  },
};

const searchBeneficiaries = ref(null);
const beneficiaries = ref([]);

const isLoading = ref(false);

const onInput = async (val) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    beneficiaries.value = await listStore.fetchBeneficiaries({
      keyword: val,
    });
    isLoading.value = false;
  } else {
    beneficiaries.value = [];
  }
};

const form = ref({});

const backToOverview = () => {
  router.push("/livelihood/overview");
};

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  return "required" in rules[field];
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

const handleDelete = async () => {
  await livelihoodStore.deleteLivelihood({ id: route.params.id });
};

onMounted(async () => {
  const data = await livelihoodStore.getLivelihood({
    id: route.params.id,
  });
  form.value = data;
  form.value.date = new Date(data.date);
  await listStore.fetchCourses();
  await listStore.fetchBranches();
  await listStore.fetchEmployabilityOfficers(true);
  await listStore.fetchCompanies();
  await listStore.fetchEmplFields();
  await listStore.fetchIscoCodes();
  await listStore.fetchProfessionalFields();
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="6">
        <h1 class="text-employability">Edit Livelihood</h1>
      </v-col>
      <v-col cols="6" class="d-flex justify-end align-center">
        <v-btn
          size="small"
          color="black"
          variant="text"
          class="mr-5"
          @click="backToOverview"
        >
          <v-icon>mdi-chevron-left</v-icon> back to overview
        </v-btn>
        <v-btn color="employability" size="large" @click="submitForm">
          <v-icon icon="mdi-content-save" /> Save Changes
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-text-field
            v-model="form.name"
            name="name"
            :error-messages="getErrorMessages('name').value"
            hide-details="auto"
            @blur="v$.name.$touch()"
          >
            <template #label>
              Title
              <span v-if="isRequired('name')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-text-field>

          <v-autocomplete
            v-model="form.beneficiary"
            v-model:search-input="searchBeneficiaries"
            :loading="isLoading"
            return-object
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="beneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            :error-messages="getErrorMessages('beneficiary').value"
            @update:search="(val) => onInput(val)"
            @blur="v$.beneficiary.$touch()"
          >
            <template #label>
              Beneficiary
              <span v-if="isRequired('beneficiary')" class="ml-1 red--text">
                <strong> *</strong>
              </span>
            </template>
          </v-autocomplete>

          <Autocomplete
            v-model="form.branch"
            label="Odyssea Branch"
            :items="branches"
            :multiple="false"
            :chips="false"
            :error-messages="getErrorMessages('branch').value"
            :is-required="isRequired('branch')"
            @blur="v$.branch.$touch()"
          />

          <Autocomplete
            v-model="form.responsibleOfficer"
            label="Responsible"
            :items="emplOfficers"
            :multiple="false"
            :chips="false"
            :error-messages="getErrorMessages('responsibleOfficer').value"
            :is-required="isRequired('responsibleOfficer')"
            @blur="v$.responsibleOfficer.$touch()"
          />

          <v-autocomplete
            v-model="form.completedCourse"
            return-object
            item-title="name"
            item-value="id"
            :items="courses"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
          >
            <template #label>
              What is the training you completed at Odyssea?
            </template>
          </v-autocomplete>

          <v-date-input
            v-model="form.date"
            variant="outlined"
            prepend-icon=""
            placeholder="DD/MM/YYYY"
            prepend-inner-icon="$calendar"
            hide-details="auto"
            :error-messages="getErrorMessages('date').value"
            @blur="v$.date.$touch()"
          >
            <template #label>
              Date
              <span v-if="isRequired('date')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-date-input>

          <v-select
            v-model="form.currentlyWorking"
            :items="currentlyWorkingStatuses"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
            :error-messages="getErrorMessages('currentlyWorking').value"
            :is-required="isRequired('currentlyWorking')"
            @blur="v$.currentlyWorking.$touch()"
          >
            <template #label>
              Are you currently working?
              <span
                v-if="isRequired('currentlyWorking')"
                class="ml-1 red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-select>

          <v-number-input
            v-if="
              form.currentlyWorking &&
              (form.currentlyWorking.value == 'FOUND_JOB_LOOKING_FOR_BETTER' ||
                form.currentlyWorking.value == 'FOUND_JOB')
            "
            v-model="form.salary"
            :reverse="false"
            controlVariant="default"
            label="Salary per month"
            :hideInput="false"
            inset
            variant="filled"
          ></v-number-input>

          <template v-if="form.currentlyWorking?.value == 'FOUND_JOB'">
            <v-divider />

            <RadioGroup
              v-model="form.odysseaSupport"
              label="Did Odyssea support him/her in finding employment/up-skilling/scholarship?"
            />

            <v-select
              v-model="form.employmentStatus"
              :items="employmentStatuses"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label>
                What is your current employment status?
              </template>
            </v-select>

            <v-select
              v-model="form.jobFoundMethod"
              :items="jobFoundMethods"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label> How did you find the job? </template>
            </v-select>

            <v-date-input
              v-model="form.jobStartDate"
              variant="outlined"
              prepend-icon=""
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              hide-details="auto"
            >
              <template #label> When did you start the job? </template>
            </v-date-input>

            <v-date-input
              v-model="form.jobEndDate"
              variant="outlined"
              prepend-icon=""
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              hide-details="auto"
            >
              <template #label> Date that ended the job </template>
            </v-date-input>

            <v-text-field
              v-model="form.jobTitle"
              name="jobTitle"
              hide-details="auto"
            >
              <template #label> What is your job title? </template>
            </v-text-field>

            <Autocomplete
              v-model="form.company"
              label="Company that hired him"
              :items="companies"
              :multiple="false"
              :chips="false"
            />

            <v-text-field
              v-model="form.otherCompany"
              name="jobTitle"
              hide-details="auto"
            >
              <template #label>
                Other company not in our list - can add the name and the
                telephone number of the supervisor
              </template>
            </v-text-field>

            <v-select
              v-model="form.trainingRelation"
              :items="trainingRelationStatuses"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label> Relation to the training </template>
            </v-select>

            <v-select
              v-model="form.employmentField"
              :items="emplFields"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label> In what field they found employment? </template>
            </v-select>

            <Autocomplete
              v-model="form.iscoCodeFour"
              label="ISCO-08 Code Level 4"
              :items="iscoCodes"
              :multiple="false"
              :chips="false"
            />

            <Autocomplete
              v-model="form.iscoCodeTwo"
              label="ISCO-08 Code Level 2"
              :items="iscoCodes"
              :multiple="false"
              :chips="false"
            />

            <v-select
              v-model="form.contractType"
              :items="contractTypes"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label> What is your type of contract? </template>
            </v-select>

            <v-text-field
              v-if="form.contractType?.value === 'OTHER'"
              v-model="form.otherContractType"
              name="otherContractType"
              hide-details="auto"
            >
              <template #label> Please specify </template>
            </v-text-field>

            <v-select
              v-model="form.contractDuration"
              :items="contractDurations"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label>
                What is the duration of the contract?
              </template>
            </v-select>

            <RadioGroup
              v-model="form.socialInsurance"
              label="Do you have social insurance?"
            />

            <v-select
              v-model="form.salaryRange"
              :items="salaryRange"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label> What is your range of salary? </template>
            </v-select>

            <v-text-field
              v-if="form.salaryRange?.value === 'OTHER'"
              v-model="form.otherSalaryRange"
              name="otherSalaryRange"
              hide-details="auto"
            >
              <template #label> Please specify </template>
            </v-text-field>

            <v-select
              v-model="form.workEnvironmentSatisfaction"
              :items="workSatisfactionOptions"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label>
                Are you satisfied with your work environment and overall working
                experience?
              </template>
            </v-select>
          </template>

          <template v-if="form.currentlyWorking?.value == 'NO_BUT_LOOKING'">
            <v-divider />
            <v-select
              v-model="form.applicationCount"
              :items="countOptions"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label>
                How many job applications have you sent the last 3 months?
              </template>
            </v-select>

            <v-select
              v-model="form.invitationCount"
              :items="countOptions"
              clearable
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            >
              <template #label>
                How many interview invitations did you receive the last 3
                months?
              </template>
            </v-select>

            <v-text-field
              v-model="form.noFeedbackComment"
              name="noFeedbackComment"
              hide-details="auto"
            >
              <template #label>
                In case you did not receive feedback, what do you think went
                wrong?
              </template>
            </v-text-field>
          </template>

          <v-divider />

          <v-date-input
            v-if="form.currentlyWorking?.value != 'FOUND_JOB'"
            v-model="form.jobEndDate"
            variant="outlined"
            prepend-icon=""
            placeholder="DD/MM/YYYY"
            prepend-inner-icon="$calendar"
            hide-details="auto"
          >
            <template #label> Date that ended the job </template>
          </v-date-input>

          <Autocomplete
            v-if="form.currentlyWorking?.value != 'FOUND_JOB'"
            v-model="form.company"
            label="Company that hired him"
            :items="companies"
            :multiple="false"
            :chips="false"
          />

          <v-select
            v-if="form.currentlyWorking?.value != 'FOUND_JOB'"
            v-model="form.employmentField"
            :items="emplFields"
            clearable
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> In what field they found employment? </template>
          </v-select>

          <v-divider />

          <v-text-field
            v-if="form.currentlyWorking?.value == 'NO_NOT_LOOKING'"
            v-model="form.noSearchReason"
            name="noSearchReason"
            hide-details="auto"
          >
            <template #label>
              Why you are not currently searching for a job?
            </template>
          </v-text-field>

          <Autocomplete
            v-model="form.suitableJobs"
            label="What are the suitable jobs for him/her?"
            :items="professionalFields"
            :multiple="true"
            :chips="true"
          />

          <VuetifyTiptap
            v-model="form.comment"
            label="Comment"
            rounded
            :min-height="200"
            :extensions="extensions"
          />
        </v-form>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="12" class="d-flex justify-end">
        <DeletePrompt @confirm="handleDelete" />
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-employability));
}
.flex-50 {
  flex: 0 0 50%;
}
</style>
